import React, { useEffect, useRef, useState } from "react";
import {
  Input,
  Switch,
  Checkbox,
  cn,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useNarrowViewport,
  VIEWPORT_LG,
} from "@semo/shared-ui";
import {
  ImageWithFallback,
  Product,
  calculateDiscountRate,
} from "@semo/shared-product";
import { TextHighlight } from "@semo/shared-ui";
import { Info, Loader2, Package } from "@semo/icons";
import { toast } from "sonner";

import { StorefrontPriceInput } from "./StorefrontPriceInput";
import {
  checkSupplierPriceInput,
  formatPriceInput,
} from "./supplierPriceInput";

/**
 * 단가 열들의 공통 폭.
 *
 * 「나의 공급가」와 쇼핑몰 단가는 나란히 놓고 **비교하며 입력하는 값**이라 폭이 다르면
 * 눈이 걸린다. 한 곳에서 정해 두 열이 따로 흘러가지 않게 한다.
 *
 * 이 표는 `table-fixed` + `colgroup` 이라 **폭은 `<col>` 이 정한다** — `th` 에 붙인
 * `w-*` 는 무시된다. 그래서 값이 아니라 `<col>` 에 준다.
 */
const PRICE_COL_WIDTH = "w-[13%]";

interface VendorProductTableProps {
  products: Product[];
  isLoading: boolean;
  showOnlyMyProducts: boolean;
  density?: "default" | "compact";
  searchTerm?: string;
  pendingTogglingProductIds?: Set<string>;

  onPriceChange: (productId: string, price: number) => void;
  onToggleSelling: (
    productId: string,
    currentStatus: boolean,
    price: number,
  ) => void;

  // Row selection (select-all lives in the bulk toolbar, not the table header)
  selectedIds: Set<string>;
  onToggleSelect: (productId: string) => void;

  /**
   * 목록이 비었을 때 문구. 지금 걸린 필터(판매중·판매중지·브랜드)에 맞춰 부모가 정한다.
   * 없으면 «내 상품만 보기» 여부로 기본 문구를 쓴다.
   */
  emptyState?: { title: string; description: string };

  // 행별 «자동견적»·«알림톡» 스위치는 뺐다(GS 검수 V-07). 저장되지 않는 화면 전용 상태라
  // 켜 둔 것처럼 보여도 새로고침하면 꺼졌다. 품목별 저장 기능은 서버에 없다 — 자동견적
  // 대상 판정(KOLAS 시험 범위)을 바꾸는 일이라 여기서 만들지 않는다.

  /**
   * 마스터가 내 품목을 허용한 쇼핑몰. 열은 여기서 생긴다 — 허용이 없으면 열도 없다.
   * 값을 넣는 순간 그 쇼핑몰에 노출되고, 비워 두면 허용돼 있어도 안 팔린다.
   */
  storefronts?: { storefrontId: string; name: string; includeVat?: boolean }[];
  storefrontPriceOf?: (storefrontId: string, itemId: string) => number | null;
  /** 마스터가 허용한 조합인지. 허용 전에는 값을 넣을 수 없다(서버가 403 을 준다) */
  storefrontIsAllowed?: (storefrontId: string, itemId: string) => boolean;
  onStorefrontRequest?: (storefrontId: string, itemId: string) => void;
  onStorefrontPriceChange?: (
    storefrontId: string,
    itemId: string,
    price: number,
  ) => void;
}

interface PriceInputProps {
  value: number;
  onChange: (value: number) => void;
  /** 입력 중인 값. 형식이 틀린 동안은 undefined — 판매 스위치가 틀린 값을 쓰지 않게. */
  onType: (value: number | undefined) => void;
  /**
   * 저장 직전 행 단위 검사(예: 판매중인데 0원). 문구를 돌려주면 저장하지 않고 되돌린다.
   */
  validateCommit?: (value: number) => string | null;
  density?: "default" | "compact";
}

/**
 * 나의 공급가 입력칸.
 *
 * 예전에는 숫자 아닌 글자를 **말없이 지워**(`12.5`→`125`, `-500`→`500`) 10배 오입력이 그대로
 * 저장됐고 상한도 없었다(GS 검수 V-14). 이제는 고쳐 쓰지 않고 칸 아래에 사유를 보여 주며,
 * 그 상태로 칸을 벗어나면 저장하지 않고 원래 값으로 되돌린 뒤 토스트로 알린다.
 */
export const PriceInput: React.FC<PriceInputProps> = ({
  value,
  onChange,
  onType,
  validateCommit,
  density = "default",
}) => {
  const [localValue, setLocalValue] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const resetToSaved = () => {
    setLocalValue(value === 0 ? "" : value.toLocaleString());
    setError(null);
  };

  useEffect(() => {
    setLocalValue(value === 0 ? "" : value.toLocaleString());
    setError(null);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    const check = checkSupplierPriceInput(text);
    if (check.ok) {
      setLocalValue(formatPriceInput(text, check.value));
      setError(null);
      onType(check.value);
    } else {
      // 고쳐 쓰지 않는다 — 입력한 그대로 두고 왜 안 되는지 보여 준다.
      setLocalValue(text);
      setError(check.message);
      onType(undefined);
    }
  };

  const handleCommit = () => {
    const check = checkSupplierPriceInput(localValue);
    if (!check.ok) {
      toast.error(`공급가를 저장하지 않았습니다. ${check.message}`);
      resetToSaved();
      onType(undefined);
      return;
    }
    if (check.value === value) return;

    const rejection = validateCommit?.(check.value) ?? null;
    if (rejection) {
      toast.error(rejection);
      resetToSaved();
      onType(undefined);
      return;
    }
    onChange(check.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      resetToSaved();
      onType(undefined);
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const inputs = Array.from(
        document.querySelectorAll<HTMLInputElement>(
          'input[data-price-input="true"]',
        ),
      );
      const index = inputs.indexOf(e.currentTarget);
      if (e.key === "ArrowDown" && index < inputs.length - 1) {
        inputs[index + 1]?.focus();
      } else if (e.key === "ArrowUp" && index > 0) {
        inputs[index - 1]?.focus();
      }
    }
  };

  return (
    <div className="relative group">
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground group-focus-within:text-primary transition-colors">
        ₩
      </span>
      <Input
        type="text"
        inputMode="numeric"
        data-price-input="true"
        aria-label="나의 공급가"
        aria-invalid={error ? true : undefined}
        className={cn(
          "pl-5 text-right transition-all duration-200 bg-white hover:border-primary/60 focus-visible:ring-primary",
          density === "compact" ? "h-7 text-xs" : "h-8 text-sm",
          localValue === "" ? "text-muted-foreground" : "font-semibold",
          error && "border-red-500 focus-visible:ring-red-500",
        )}
        placeholder="0"
        value={localValue}
        onChange={handleChange}
        onBlur={handleCommit}
        onKeyDown={handleKeyDown}
      />
      {error ? (
        <p
          role="alert"
          className="mt-0.5 text-2xs leading-tight text-red-600"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
};

/** 판매중인 행을 0원으로 저장하면 «0원에 판매중» 이 된다 — 저장 전에 막는다. */
const sellingRowPriceRule =
  (isSelling: boolean) =>
  (next: number): string | null =>
    isSelling && next <= 0
      ? "판매중인 상품의 공급가는 1원 이상이어야 합니다. 판매를 멈추려면 ‘판매 상태’를 꺼주세요."
      : null;

interface VendorProductCardsProps extends VendorProductTableProps {
  onPriceType: (productId: string, value: number | undefined) => void;
  priceRefs: React.MutableRefObject<Record<string, number | undefined>>;
}

/** 빈 목록 문구 — 부모가 정한 것이 있으면 그것, 없으면 «내 상품만 보기» 여부로. */
const resolveEmptyState = (
  emptyState: VendorProductTableProps["emptyState"],
  showOnlyMyProducts: boolean,
): { title: string; description: string } =>
  emptyState ??
  (showOnlyMyProducts
    ? {
        title: "등록한 내 상품이 없습니다.",
        description:
          "공급가를 입력하고 ‘판매 상태’를 켜면 내 상품으로 등록됩니다.",
      }
    : {
        title: "검색 결과가 없습니다.",
        description: "검색어를 확인하거나 카테고리를 변경해보세요.",
      });

/**
 * 모바일·태블릿 카드 목록.
 *
 * 표의 열을 그대로 담되 세로로 접는다 — 열을 빼면 휴대폰에서만 못 고치는 값이
 * 생긴다(특히 '나의 공급가'와 판매 상태 스위치는 이 화면의 존재 이유다).
 */
const VendorProductCards: React.FC<VendorProductCardsProps> = ({
  products,
  isLoading,
  showOnlyMyProducts,
  density = "default",
  searchTerm,
  pendingTogglingProductIds = new Set(),
  onPriceChange,
  onToggleSelling,
  selectedIds,
  onToggleSelect,
  emptyState,
  onPriceType,
  priceRefs,
}) => {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 p-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-24 animate-pulse rounded-lg bg-muted/40" />
        ))}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex h-[360px] flex-col items-center justify-center gap-3 px-6 text-center text-muted-foreground">
        <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-muted/50">
          <Info className="h-6 w-6 text-muted-foreground/50" />
        </div>
        <p className="text-lg font-medium text-foreground/80">
          {resolveEmptyState(emptyState, showOnlyMyProducts).title}
        </p>
        <p className="text-sm">
          {resolveEmptyState(emptyState, showOnlyMyProducts).description}
        </p>
      </div>
    );
  }

  return (
    <ul className="divide-y">
      {products.map((product) => {
        const myPrice = product.myPrice ?? 0;
        const isSelling = !!product.isSelling;
        const hasMasterPrice = product.price > 0;
        const isSelected = selectedIds.has(product.id);
        const isPending = pendingTogglingProductIds.has(product.id);
        const { formattedRate, isDiscount } = calculateDiscountRate(
          product.price,
          myPrice,
        );

        return (
          <li
            key={product.id}
            className={cn(
              "px-3 py-3",
              isSelected ? "bg-primary/10" : "bg-card",
            )}
          >
            {/* 상품정보 + 선택 */}
            <div className="flex items-start gap-2">
              <Checkbox
                className="mt-1 shrink-0"
                checked={isSelected}
                onCheckedChange={() => onToggleSelect(product.id)}
                aria-label={`${product.name} 선택`}
              />
              <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded border bg-muted text-muted-foreground/40">
                {product.imageUrl ? (
                  <ImageWithFallback
                    src={product.imageUrl}
                    alt={product.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <Package className="h-5 w-5" />
                )}
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <TextHighlight
                  text={product.name}
                  keyword={searchTerm}
                  className="text-sm font-medium leading-tight line-clamp-2"
                />
                {product.code && (
                  <span className="font-mono text-2xs text-muted-foreground">
                    {product.code}
                  </span>
                )}
                {product.categoryPath && (
                  <span className="inline-flex w-fit max-w-full items-center gap-1 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-2xs text-slate-500">
                    <Info className="h-3 w-3 shrink-0" />
                    <span className="truncate">{product.categoryPath}</span>
                  </span>
                )}
              </div>
            </div>

            {/* 제조사/브랜드 · 규격/단위 · 소비자 단가 */}
            <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
              <div className="min-w-0">
                <dt className="text-2xs font-medium uppercase text-muted-foreground">
                  제조사/브랜드
                </dt>
                <dd className="truncate text-foreground">
                  {product.brand ?? product.manufacturer ?? "-"}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-2xs font-medium uppercase text-muted-foreground">
                  상세규격/단위
                </dt>
                <dd className="flex flex-wrap items-center gap-1 text-foreground">
                  <span className="truncate">{product.spec1 ?? "-"}</span>
                  {product.unit && (
                    <span className="inline-flex shrink-0 items-center rounded border border-slate-200 px-1.5 text-2xs text-slate-500">
                      {product.unit}
                    </span>
                  )}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-2xs font-medium uppercase text-muted-foreground">
                  소비자 단가
                </dt>
                <dd className="font-medium text-slate-600">
                  {hasMasterPrice ? `${product.price.toLocaleString()}원` : "-"}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-2xs font-medium uppercase text-muted-foreground">
                  할인율
                </dt>
                <dd>
                  {hasMasterPrice && myPrice > 0 ? (
                    <span
                      className={cn(
                        "font-semibold",
                        isDiscount ? "text-emerald-600" : "text-gray-500",
                      )}
                    >
                      {formattedRate}%
                    </span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </dd>
              </div>
            </dl>

            {/* 나의 공급가 — 이 화면에서 가장 자주 만지는 값이라 한 줄을 통째로 준다 */}
            <div className="mt-2.5">
              <span className="text-2xs font-medium uppercase text-primary">
                나의 공급가 (VAT 별도)
              </span>
              <div className="mt-1">
                <PriceInput
                  value={myPrice}
                  onChange={(val) => onPriceChange(product.id, val)}
                  onType={(val) => onPriceType(product.id, val)}
                  validateCommit={sellingRowPriceRule(isSelling)}
                  density={density}
                />
              </div>
            </div>

            {/* 판매상태 */}
            <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-2 border-t pt-2.5 text-xs">
              <div className="flex items-center gap-1.5">
                {isPending && (
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                )}
                <Switch
                  checked={isSelling}
                  disabled={isPending}
                  aria-label="판매 상태"
                  data-testid="selling-switch"
                  onCheckedChange={() => {
                    const currentTypedPrice =
                      priceRefs.current[product.id] ?? myPrice;
                    onToggleSelling(product.id, isSelling, currentTypedPrice);
                  }}
                />
                <span
                  className={cn(
                    "font-medium whitespace-nowrap",
                    isSelling ? "text-primary-text" : "text-muted-foreground",
                  )}
                >
                  {isSelling ? "판매중" : "판매중지"}
                </span>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export const VendorProductTable: React.FC<VendorProductTableProps> = ({
  products,
  isLoading,
  showOnlyMyProducts,
  density = "default",
  searchTerm,
  pendingTogglingProductIds = new Set(),
  onPriceChange,
  onToggleSelling,
  selectedIds,
  onToggleSelect,
  emptyState,
  storefronts = [],
  storefrontPriceOf = () => null,
  storefrontIsAllowed = () => true,
  onStorefrontRequest = () => undefined,
  onStorefrontPriceChange = () => undefined,
}) => {
  const priceRefs = useRef<Record<string, number | undefined>>({});
  const handlePriceType = (productId: string, value: number | undefined) => {
    priceRefs.current[productId] = value;
  };

  const isCompact = density === "compact";
  const rowDensityClass = isCompact
    ? "[&>td]:py-1.5 [&>td]:px-2 text-xs"
    : "[&>td]:py-3 [&>td]:px-3 text-sm";
  const headerDensityClass = isCompact
    ? "h-9 text-2xs px-2"
    : "h-11 text-xs px-3";

  // 「검색 결과 없음」·로딩 행이 표 전체를 가로지르려면 실제 열 수를 따라가야 한다.
  // 쇼핑몰 열은 허용된 수만큼 늘어나므로 고정값이면 한 칸씩 어긋난다.
  const COL_COUNT = 8 + storefronts.length;

  // 좁은 화면(<lg)은 표 대신 카드. 8열은 어떻게 줄여도 태블릿 세로폭에 들어가지 않는다.
  const isNarrow = useNarrowViewport(VIEWPORT_LG);

  if (isNarrow) {
    return (
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        <div className="relative w-full flex-1 overflow-y-auto bg-card">
          <VendorProductCards
            products={products}
            isLoading={isLoading}
            showOnlyMyProducts={showOnlyMyProducts}
            density={density}
            searchTerm={searchTerm}
            pendingTogglingProductIds={pendingTogglingProductIds}
            onPriceChange={onPriceChange}
            onToggleSelling={onToggleSelling}
            selectedIds={selectedIds}
            onToggleSelect={onToggleSelect}
            emptyState={emptyState}
            onPriceType={handlePriceType}
            priceRefs={priceRefs}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-hidden flex flex-col min-h-0">
      {/*
        `table-fixed` + `colgroup` — 열 너비를 내용이 아니라 이 표가 정한다.

        예전에는 `min-w-[1140px]` 로 최소 폭을 박아 두고 넘치면 가로 스크롤에 맡겼는데,
        이 화면은 c-market 안에서 iframe 모달로 열린다. 모달이 창보다 좁으면 오른쪽 열
        (할인율 등)이 잘려서 아예 닿지 못했다. 이제 어떤 폭에서도 열 8개가
        전부 들어오고, 남는 폭은 상품정보(품명)가 가져간다.
      */}
      <div className="relative w-full flex-1 overflow-y-auto overflow-x-hidden bg-card">
        <table className="w-full table-fixed caption-bottom border-separate border-spacing-0">
          <colgroup>
            <col className="w-10" />
            <col className="w-[100px]" />
            {/* 상품정보 — 남는 폭 전부 */}
            <col />
            <col className="w-[12%]" />
            <col className="w-[13%]" />
            <col className="w-[10%]" />
            {/* 나의 공급가 — 쇼핑몰 단가와 같은 폭. 나란히 비교하며 입력하는 값이다. */}
            <col className={PRICE_COL_WIDTH} />
            {storefronts.map((sf) => (
              <col key={sf.storefrontId} className={PRICE_COL_WIDTH} />
            ))}
            <col className="w-[64px]" />
          </colgroup>
          <thead className="sticky top-0 z-40 bg-card shadow-sm ring-1 ring-border">
            <tr className="hover:bg-transparent font-medium text-muted-foreground">
              <th
                className={cn(
                  "border-b bg-card text-center align-middle px-1",
                  headerDensityClass,
                )}
              />
              <th
                className={cn(
                  "border-b bg-card text-center px-1",
                  headerDensityClass,
                )}
              >
                판매상태
              </th>
              <th
                className={cn("border-b bg-card text-left", headerDensityClass)}
              >
                상품정보
              </th>
              <th
                className={cn(
                  "border-b bg-card text-center",
                  headerDensityClass,
                )}
              >
                제조사/브랜드
              </th>
              <th
                className={cn("border-b bg-card text-left", headerDensityClass)}
              >
                상세규격/단위
              </th>
              <th
                className={cn(
                  "border-b bg-card text-center px-1",
                  headerDensityClass,
                )}
              >
                소비자 단가
              </th>
              <th
                className={cn(
                  "border-b bg-card text-center px-1",
                  headerDensityClass,
                )}
              >
                <span className="text-xs font-semibold text-primary">
                  나의 공급가(VAT별도)
                </span>
              </th>
              {storefronts.map((sf) => (
                <th
                  key={sf.storefrontId}
                  className={cn(
                    "border-b bg-card text-center px-1",
                    headerDensityClass,
                  )}
                >
                  <span className="text-xs font-semibold text-emerald-700">
                    {sf.name}
                    {/* 몰마다 표시 축이 다를 수 있다. 무슨 값을 넣는지가 헤더에 있어야 한다. */}
                    <span className="ml-1 font-normal text-2xs text-muted-foreground">
                      ({sf.includeVat ? "VAT 포함" : "VAT 별도"})
                    </span>
                  </span>
                </th>
              ))}
              <th
                className={cn(
                  "border-b bg-card text-center px-1",
                  headerDensityClass,
                )}
              >
                할인율
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className={isCompact ? "h-12" : "h-16"}>
                  <td colSpan={COL_COUNT} className={isCompact ? "p-2" : "p-3"}>
                    <div className="h-8 w-full bg-muted/40 rounded animate-pulse" />
                  </td>
                </tr>
              ))
            ) : products.length === 0 ? (
              <tr>
                <td
                  colSpan={COL_COUNT}
                  className="text-center h-[360px] text-muted-foreground"
                >
                  <div className="flex flex-col items-center justify-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center mb-2">
                      <Info className="w-6 h-6 text-muted-foreground/50" />
                    </div>
                    <p className="text-lg font-medium text-foreground/80">
                      {resolveEmptyState(emptyState, showOnlyMyProducts).title}
                    </p>
                    <p className="text-sm">
                      {
                        resolveEmptyState(emptyState, showOnlyMyProducts)
                          .description
                      }
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              products.map((product) => {
                const myPrice = product.myPrice ?? 0;
                const isSelling = !!product.isSelling;
                const hasMasterPrice = product.price > 0;
                const isSelected = selectedIds.has(product.id);
                const isPending = pendingTogglingProductIds.has(product.id);

                const { formattedRate, isDiscount } = calculateDiscountRate(
                  product.price,
                  myPrice,
                );

                return (
                  <tr
                    key={product.id}
                    className={cn(
                      "group transition-colors [&>td]:border-b [&>td]:border-slate-100",
                      rowDensityClass,
                      isSelected ? "bg-primary/10" : "bg-white",
                    )}
                  >
                    {/* checkbox */}
                    <td className="text-center align-middle">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => onToggleSelect(product.id)}
                        aria-label={`${product.name} 선택`}
                      />
                    </td>

                    {/* 판매상태 — 1줄 */}
                    <td className="align-middle">
                      <div className="flex items-center justify-center gap-1.5">
                        {isPending && (
                          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                        )}
                        <Switch
                          checked={isSelling}
                          disabled={isPending}
                          aria-label="판매 상태"
                          data-testid="selling-switch"
                          onCheckedChange={() => {
                            const currentTypedPrice =
                              priceRefs.current[product.id] ?? myPrice;
                            onToggleSelling(
                              product.id,
                              isSelling,
                              currentTypedPrice,
                            );
                          }}
                        />
                        <span
                          className={cn(
                            "text-2xs font-medium whitespace-nowrap",
                            isSelling
                              ? "text-primary-text"
                              : "text-muted-foreground",
                          )}
                        >
                          {isSelling ? "판매중" : "판매중지"}
                        </span>
                      </div>
                    </td>

                    {/* 상품정보 — 이미지가 이름+코드+카테고리 칩 전체 기준 세로 중앙 */}
                    <td className="align-middle">
                      <div className="flex items-center gap-3">
                        <div
                          className={cn(
                            "rounded overflow-hidden bg-muted border flex items-center justify-center shrink-0 text-muted-foreground/40",
                            isCompact ? "h-7 w-7" : "h-10 w-10",
                          )}
                        >
                          {product.imageUrl ? (
                            <ImageWithFallback
                              src={product.imageUrl}
                              alt={product.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <Package
                              className={isCompact ? "w-3.5 h-3.5" : "w-5 h-5"}
                            />
                          )}
                        </div>
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="font-medium cursor-default">
                                  <TextHighlight
                                    text={product.name}
                                    keyword={searchTerm}
                                    className="line-clamp-2 leading-tight"
                                  />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                className="max-w-[300px] break-words"
                              >
                                {product.name}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {!isCompact && product.code && (
                            <span className="text-xs text-muted-foreground font-mono">
                              {product.code}
                            </span>
                          )}
                          {!isCompact && product.categoryPath && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="w-fit max-w-full inline-flex items-center gap-1 truncate rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-2xs text-slate-500 cursor-default">
                                    <Info className="w-3 h-3 shrink-0" />
                                    <span className="truncate">
                                      {product.categoryPath}
                                    </span>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                  {product.categoryPath}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* 제조사/브랜드 — 1줄, 가운데 */}
                    <td className="align-middle text-center text-muted-foreground">
                      <span className="block truncate">
                        {product.brand ?? product.manufacturer ?? "-"}
                      </span>
                    </td>

                    {/* 상세규격/단위 — 왼쪽, 단위는 은은하게 */}
                    <td className="align-middle">
                      <div className="flex flex-col gap-0.5">
                        {product.spec1 && (
                          <div className="flex items-center gap-1.5 truncate">
                            <span className="text-[0.8em] text-muted-foreground shrink-0">
                              규격
                            </span>
                            <span className="truncate text-foreground">
                              {product.spec1}
                            </span>
                          </div>
                        )}
                        {product.unit && (
                          <span className="inline-flex w-fit items-center rounded border border-slate-200 px-1.5 py-0 text-2xs text-slate-500">
                            {product.unit}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* 소비자단가 — 가운데 */}
                    <td className="text-center align-middle font-medium text-slate-600">
                      {hasMasterPrice
                        ? `${product.price.toLocaleString()}원`
                        : "-"}
                    </td>

                    {/* 나의 공급가 — 헤더 글씨만 블루, 칸 배경은 흰색 */}
                    <td className="align-middle">
                      <PriceInput
                        value={myPrice}
                        onChange={(val) => onPriceChange(product.id, val)}
                        onType={(val) => handlePriceType(product.id, val)}
                        validateCommit={sellingRowPriceRule(isSelling)}
                        density={density}
                      />
                    </td>

                    {storefronts.map((sf) =>
                      /*
                       * 허용되지 않은 조합에 입력칸을 주면 값을 넣는 순간 403 이 난다.
                       * 빈 칸은 «아직 안 적었다» 와 «허용되지 않았다» 를 구분하지 못하므로
                       * 여기서 갈라 준다 — 뒤엣것은 요청 버튼이 답이다.
                       */
                      !storefrontIsAllowed(sf.storefrontId, product.id) ? (
                        <td key={sf.storefrontId} className="text-center align-middle">
                          <button
                            type="button"
                            onClick={() =>
                              onStorefrontRequest(sf.storefrontId, product.id)
                            }
                            className="rounded border border-dashed border-border px-1.5 py-1 text-2xs text-muted-foreground hover:border-emerald-400 hover:text-emerald-700"
                            title="이 쇼핑몰에 아직 승인되지 않은 품목입니다. 승인을 요청합니다."
                          >
                            승인 요청
                          </button>
                        </td>
                      ) : (
                      <td key={sf.storefrontId} className="align-middle">
                        <StorefrontPriceInput
                          value={storefrontPriceOf(sf.storefrontId, product.id)}
                          density={density}
                          onCommit={(next) =>
                            onStorefrontPriceChange(
                              sf.storefrontId,
                              product.id,
                              next,
                            )
                          }
                        />
                      </td>
                      ),
                    )}

                    {/* 할인율 — 할인 시 칸 자체를 연두 */}
                    <td
                      className={cn(
                        "text-center align-middle",
                        hasMasterPrice &&
                          myPrice > 0 &&
                          isDiscount &&
                          "bg-emerald-50/50",
                      )}
                    >
                      {hasMasterPrice && myPrice > 0 ? (
                        <span
                          className={cn(
                            "font-semibold",
                            isDiscount ? "text-emerald-600" : "text-gray-500",
                          )}
                        >
                          {formattedRate}%
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>

                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
